import { Box, GridLegacy as Grid, Stack, useTheme } from '@mui/material'
import { DecoratedValue, InfoTooltip, Typography } from 'ComponentLibrary'
import { TypographyVariantTypes } from '../../typography/Typography'

type FormInputLabelVariant = 'left' | 'right' | 'top'

type FormInputLabelProps = {
    label: string
    xs?: number
    position?: number
    fillEnd?: boolean
    fillStart?: boolean
    variant?: FormInputLabelVariant
    typographyVariant?: TypographyVariantTypes
    id?: string
    decorator?: string
    required?: boolean
    className?: string
    color?: string
    info?: string
    styles?: React.CSSProperties
}

const VARIANT_CONFIGURATION = {
    left: { justifyContent: undefined, textAlign: undefined, typographyVariant: 'bodyTextLarge' },
    right: { justifyContent: undefined, textAlign: 'right', typographyVariant: 'bodyTextLarge' },
    top: { justifyContent: 'center', textAlign: 'center', typographyVariant: 'captionSmall' },
} as const

/**
 * spreads excess props to Typography to pass aria attributes
 */
const FormInputLabel = ({
    xs = 10,
    position = 0,
    label,
    variant = 'left',
    fillEnd = false,
    fillStart = false,
    typographyVariant = 'bodyTextSmall',
    id,
    decorator,
    required,
    className,
    color,
    info,
    styles,
    ...ariaAttributes
}: FormInputLabelProps) => {
    const theme = useTheme()

    return (
        <>
            {fillStart && position > 0 && <Grid xs={position} item />}
            <Grid xs={xs} item style={styles}>
                <Stack
                    direction="row"
                    gap={theme.spacing(2)}
                    sx={{
                        height: '100%',
                        alignItems: 'center',
                        minWidth: 'calc(max-content, 100%)',
                        justifyContent: mapVariantToFlexAlign[variant],
                    }}
                >
                    {!decorator && !required ? (
                        <Typography
                            className={className}
                            id={id}
                            style={{ textAlign: VARIANT_CONFIGURATION[variant].textAlign, overflowWrap: 'break-word', color }}
                            variant={typographyVariant || VARIANT_CONFIGURATION[variant].typographyVariant}
                            {...ariaAttributes}
                        >
                            {label}
                        </Typography>
                    ) : (
                        <DecoratedValue
                            startDecorator={decorator}
                            endDecorator={required ? '*' : undefined}
                            id={id}
                            className={className}
                            style={{ textAlign: VARIANT_CONFIGURATION[variant].textAlign, color }}
                            variant={typographyVariant || VARIANT_CONFIGURATION[variant].typographyVariant}
                            {...ariaAttributes}
                        >
                            {label}
                        </DecoratedValue>
                    )}
                    {info && (
                        <Box margin={'-6px'}>
                            <InfoTooltip text={info} />
                        </Box>
                    )}
                </Stack>
            </Grid>
            {fillEnd && <Grid xs={12 - xs - position} item />}
        </>
    )
}

const mapVariantToFlexAlign: Record<FormInputLabelVariant, string> = {
    left: 'start',
    right: 'end',
    top: 'center',
} as const

export default FormInputLabel
